import { tokens } from './tokens';

const c = tokens.color;

/**
 * The backoffice theme carries custom keys next to the MUI ones (general,
 * scrollbars, sidebar, toolbar, status, stepper). Shared components read them
 * directly, so a theme that omits them makes those components fail on an
 * undefined lookup. PALO is the successor backoffice theme and therefore serves
 * the same keys — with dense PALO values instead of the legacy ones.
 */
export const backofficeContract = {
    general: {
        // The PALO body sets its own padding, so pages no longer carry the
        // legacy 62px gutter.
        pageContentSpacing: '16px',
        colors: {
            activitySidebarBackground: c.surfaceAlt,
            black: c.ink,
            hint: c.ink3,
        },
        // PALO keeps every cell on the same inset, so the outer columns line up
        // with the rest of the grid instead of being pushed in.
        tableGutter: {
            outer: '10px',
            inner: '10px',
        },
    },
    scrollbars: {
        color: c.lineHard,
        backgroundColor: c.surfaceAlt,
        backgroundColorDark: c.ink,
        borderRadius: 2.5,
        width: 5,
    },
    sidebar: {
        logoWidthOpen: 81,
        logoWidthCollapsed: 40,
        widthOpen: 212,
        widthCollapsed: 56,
        iconSize: 20,
        subheaderSize: 13,
        linkItemSize: 14,
        palette: {
            background: c.chrome2,
            subheader: c.ink3,
            divider: c.line,
            text: c.ink2,
            activeLink: c.accent,
        },
    },
    searchField: {
        // The prototype's search box: a field, not a lozenge.
        borderRadius: tokens.radius.small + 'px',
        minWidth: '240px',
        paddingLeft: 10,
        paddingBlock: 5,
        fontSize: 13,
    },
    tableFooter: {
        borderTop: `1px solid ${c.line}`,
        background: c.surfaceAlt,
        paddingBlock: 0.75,
        paddingInline: 1.5,
        fontSize: 11.5,
        controlHeight: 24,
    },
    // The prototype's dense button: 30 tall, 12.5 type, the small radius.
    button: {
        minHeight: 30,
        borderRadius: tokens.radius.small,
        fontSize: 12.5,
    },
    chip: {
        // The soft pill of the prototype, so a status in a legacy list reads
        // like a status in a PALO list — no upper case, no 120px column.
        height: '19px',
        borderRadius: '10px',
        fontSize: 10.5,
        fontWeight: 620,
        letterSpacing: '0.015em',
        labelMinWidth: 0,
        listTextTransform: 'none' as const,
        listTextAlign: 'left' as const,
        listWidth: 'auto' as const,
    },
    modal: {
        // The prototype's dialog: compact left title, tight body, bordered bar.
        paperBorderRadius: '10px',
        paperBoxShadow: tokens.shadow.s3,
        titleAlign: 'left' as const,
        titlePadding: '15px 20px 13px',
        titleFontSize: '15px',
        titleFontWeight: 640,
        titleBorderBottom: `1px solid ${c.line}`,
        closeSize: '1.25rem',
        closeColor: c.ink3,
        closeOffset: '14px',
        contentPadding: '18px 20px',
        messageAlign: 'left' as const,
        messageFontSize: 13,
        messageLineHeight: '20px',
        actionsPadding: '12px 20px',
        actionsBorderTop: `1px solid ${c.line}`,
        actionTextTransform: 'none' as const,
        actionFontWeight: 540,
        actionLetterSpacing: 'normal',
    },
    toolbar: {
        // PALO headers sit on the light surface, so toolbar controls read as ink
        // instead of the legacy white-on-turquoise.
        height: '48px',
        backgroundColor: c.surface,
        color: c.ink,
        disabledColor: c.ink3,
        hoverBackgroundColor: c.surfaceAlt,
    },
    stepper: {
        height: '48px',
        progressBarSize: '120px',
    },
    status: {
        default: { color: c.ink2, backgroundColor: c.neutralSoft },
        error: { color: c.crit, backgroundColor: c.critSoft },
        primary: { color: c.accentHi, backgroundColor: c.accentSoft },
        success: { color: c.ok, backgroundColor: c.okSoft },
        warning: { color: c.warn, backgroundColor: c.warnSoft },
        info: { color: c.accentHi, backgroundColor: c.accentSoft },
    },
};
